import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Battery, Shield, Activity, TrendingUp, Recycle, ShieldAlert } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-8 py-6 max-w-7xl mx-auto border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <img src="/bess-scan-mark.png" alt="BESS SCAN" className="h-10 w-auto" />
          <span className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="text-[#0f2e4d]">BESS</span> <span className="text-red-600">SCAN</span>
          </span>
        </div>
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            className="text-gray-700 hover:text-black hover:bg-gray-100"
            onClick={() => navigate('/login')}
            data-testid="nav-login-btn"
          >
            Sign In
          </Button>
          <Button 
            className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-medium px-6"
            onClick={() => navigate('/register')}
            data-testid="nav-register-btn"
          >
            Get Started
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-8 py-20 fade-in">
        <div className="text-center space-y-8">
          <div className="inline-block">
            <div className="flex items-center space-x-2 bg-red-50 border border-red-200 rounded-full px-4 py-2 mb-6">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
              <span className="text-sm text-gray-700">End-to-End BESS Lifecycle Platform</span>
            </div>
          </div>
          
          <h1 className="text-6xl lg:text-7xl font-extrabold text-black leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Utility BESS<br/>
            <span className="bg-gradient-to-r from-red-500 to-rose-600 bg-clip-text text-transparent">Lifecycle Intelligence</span>
          </h1>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            One platform for the entire battery lifecycle — leveraging AI to monitor
            performance and safety, predict failures before they happen, defend against
            cyber and physical threats, and manage compliant end-of-life. Across LFP, NMC,
            LCO and Sodium-ion, for fixed and mobile fleets.
          </p>

          <div className="flex items-center justify-center space-x-4 pt-6">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-medium px-8 text-lg h-14 rounded-xl"
              onClick={() => navigate('/register')}
              data-testid="hero-get-started-btn"
            >
              Get Started
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-gray-300 bg-white hover:bg-gray-50 text-gray-800 font-medium px-8 text-lg h-14 rounded-xl"
              onClick={() => navigate('/login')}
              data-testid="hero-login-btn"
            >
              View Demo
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-24">
          {[
            { label: 'Active Assets', value: '20+', icon: Battery },
            { label: 'Uptime', value: '99.9%', icon: Activity },
            { label: 'Safety Score', value: '98', icon: Shield },
            { label: 'Efficiency', value: '92%', icon: TrendingUp }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-red-500 hover:shadow-lg transition-all duration-300" data-testid={`stat-card-${idx}`}>
              <stat.icon className="w-8 h-8 text-red-500 mb-3" />
              <div className="text-3xl font-bold text-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-24">
          {[
            {
              icon: Activity,
              title: 'Performance & Safety',
              description: 'Live SOC, SOH and thermal telemetry with predictive anomaly detection and early-warning for thermal runaway.'
            },
            {
              icon: ShieldAlert,
              title: 'Security & Environment',
              description: 'Cyber-physical and physical security monitoring with environmental early-warning across fixed and mobile assets.'
            },
            {
              icon: Recycle,
              title: 'End-of-Life Management',
              description: 'Disposal chain-of-custody, project management on behalf of clients, and audit-ready destruction certificates.'
            }
          ].map((feature, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-8 hover:border-red-500 hover:shadow-lg transition-all duration-300 group" data-testid={`feature-card-${idx}`}>
              <div className="w-14 h-14 rounded-xl bg-red-50 flex items-center justify-center mb-6 group-hover:bg-red-100 transition-all duration-300">
                <feature.icon className="w-7 h-7 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-black mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{feature.title}</h3>
              <p className="text-gray-700 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-24 text-center bg-red-50 border border-red-200 rounded-3xl p-12">
          <h2 className="text-4xl font-bold text-black mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Manage your fleet end-to-end</h2>
          <p className="text-gray-700 text-lg mb-8">From first charge to certified end-of-life — all in one platform</p>
          <Button 
            size="lg"
            className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-medium px-10 text-lg h-14 rounded-xl"
            onClick={() => navigate('/register')}
            data-testid="cta-get-started-btn"
          >
            Get Started Now
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-24 py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 text-center text-gray-700 text-sm">
          <p className="mb-2">© 2026 BESS SCAN. End-to-End Utility BESS Lifecycle Platform.</p>
          <p className="text-gray-600 text-xs">
            BESS SCAN is a proprietary technology platform developed by Australia's Battery Technology Investments Pty Limited (ABN 54 676 904 902).
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;